import { TranslocoPipe, provideTranslocoScope, translate } from '@jsverse/transloco';
import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatChipSelectionChange, MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoCurrencyPipe, TranslocoDecimalPipe, TranslocoPercentPipe } from '@jsverse/transloco-locale';
import { CanDirective, CapabilityNames, DynamicRouteService, HeaderService } from 'core';
import { CustomerNotificationItem, CustomersNotificationsBottomSheet, IconAlert } from 'customers';
import { downloadBlob } from 'receipts';
import {
  historyFromActivities,
  OrderActivityChange,
  TASKS_DESCRIBE,
  taskAnomalies,
  TaskDetailService,
  TaskHistoryStep,
} from 'tasks';
import { finalize } from 'rxjs';
import { OrderStatusBadge } from '../../components/order-status-badge/order-status-badge';
import { OrderDetail as OrderDetailData, OrderDetailResult, OrderDetailTask } from '../../interfaces/order-detail.model';
import { OrderProposalService } from '../../services/order-proposal.service';
import { OrderDetailService } from '../../services/order-detail.service';
import { orderTagLabel } from '../../utils/order.utils';

interface OrderDetailProduct {
  id: string;
  nome: string | null;
  sku: string | null;
  imballo: string | null;
  unitaFatturabile: number;
  unitaMisura: string | null;
  quantita: number;
  importo: number;
}

// Contenuto delle schede: una commessa (sotto-ordine), oppure l'ordine in App finché ERP non ne ha create
interface OrderDetailView {
  id: string;
  label: string | null;
  tag: string;
  tasks: OrderDetailTask[];
  anomalies: string[];
  // Dalla spedizione in poi: trasportatore e data, quella di consegna appena c'è
  shipping: { trasportatore: string | null; data: string | null; consegnato: boolean } | null;
  numeroOrdine: string;
  dataInserimento: string | null;
  provvigione: number | null;
  deposito: number | null;
  products: OrderDetailProduct[];
  notes: { title: string; text: string }[];
}

const SHIPPED_STATES = ['Shipped', 'Invoiced', 'Closed'];

@Component({
  providers: [provideTranslocoScope('orders')],
  selector: 'ord-order-detail',
  imports: [TranslocoPipe,
    DatePipe,
    NgTemplateOutlet,
    MatBadgeModule,
    MatButtonModule,
    MatChipsModule,
    MatExpansionModule,
    MatTabsModule,
    TranslocoCurrencyPipe,
    TranslocoDecimalPipe,
    TranslocoPercentPipe,
    CanDirective,
    IconAlert,
    OrderStatusBadge,
  ],
  templateUrl: './order-detail.html',
  styleUrl: './order-detail.scss',
})
export class OrderDetail implements OnInit, OnDestroy {
  private readonly headerService = inject(HeaderService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dynamicRouteService = inject(DynamicRouteService);
  private readonly detailService = inject(OrderDetailService);
  private readonly proposalService = inject(OrderProposalService);
  private readonly taskDetailService = inject(TaskDetailService);

  readonly capabilityNames = CapabilityNames;

  readonly detail = signal<OrderDetailData | null>(null);
  readonly alerts = signal<CustomerNotificationItem[]>([]);
  readonly history = signal<TaskHistoryStep[]>([]);
  readonly notFound = signal(false);
  readonly busy = signal(false);

  readonly views = computed(() => {
    const detail = this.detail();
    return detail ? this.toViews(detail) : [];
  });

  private readonly selectedViewId = signal<string | null>(null);
  readonly view = computed(() => this.views().find(v => v.id === this.selectedViewId()) ?? this.views()[0] ?? null);
  readonly selectedTab = signal(0);

  // Importi come nella lista: le commesse non annullate (tutte se lo sono tutte), prima delle commesse l'ordine in App
  readonly summary = computed(() => {
    const detail = this.detail();
    if (!detail) {
      return null;
    }

    const active = detail.commesse.filter(c => c.stato !== 'Cancelled');
    const subOrders = active.length > 0 ? active : detail.commesse;
    const commissions = subOrders.map(c => c.provvigione).filter((value): value is number => value != null);

    return {
      ragioneSociale: detail.cliente?.ragioneSociale ?? detail.ordine?.ragioneSociale ?? '-',
      importo: subOrders.length > 0 ? subOrders.reduce((sum, c) => sum + c.importo, 0) : detail.ordine?.subtotale ?? 0,
      provvigione: subOrders.length > 0
        ? (commissions.length > 0 ? commissions.reduce((sum, value) => sum + value, 0) : null)
        : detail.ordine?.totaleProvvigioni ?? null,
      partitaIva: detail.cliente?.partitaIva ?? '-',
    };
  });

  // L'ultimo preventivo generato si scarica anche se scaduto
  readonly quoteAvailable = computed(() => {
    const status = this.detail()?.ordine?.preventivoStato;
    return status === 'Generato' || status === 'Scaduto';
  });

  readonly changeFieldLabels: Record<OrderActivityChange['campo'], string> = {
    get Quantita() { return translate<string>('orders.ui.quantita2'); },
    get Prezzo() { return translate<string>('orders.ui.prezzoUnitario'); },
    get PercProvvigione() { return translate<string>('orders.ui.provvigioneAgente'); },
    get PercNoProfit() { return translate<string>('orders.ui.noProfit'); },
    get PercFuoriPremio() { return translate<string>('orders.ui.fuoriPremio'); },
  };

  ngOnInit(): void {
    this.headerService.setTitle('orders.ui.dettaglioOrdine');

    // Gli ordini nati in ERP arrivano dalla lista col solo carrello
    const params = this.route.snapshot.queryParamMap;
    const idCarrello = params.get('idCarrello');
    const detail$ = idCarrello
      ? this.detailService.getDetailByCart(idCarrello)
      : this.detailService.getDetail(params.get('id') ?? '');

    detail$.subscribe({
      next: result => this.onLoaded(result),
      error: () => this.notFound.set(true),
    });
  }

  ngOnDestroy(): void {
    this.headerService.removeTitle();
  }

  // Un sotto-ordine resta sempre scelto: la scheda riparte da Informazioni
  onSubOrderChange(event: MatChipSelectionChange, id: string): void {
    if (!event.selected) {
      if (this.view()?.id === id) {
        event.source.select();
      }
      return;
    }

    this.selectedViewId.set(id);
    this.selectedTab.set(0);
  }

  openAlerts(): void {
    this.bottomSheet.open(CustomersNotificationsBottomSheet, { data: { notifications: this.alerts() } });
  }

  openTask(task: OrderDetailTask): void {
    const path = this.dynamicRouteService.getRoutePath(this.router, TASKS_DESCRIBE, 'task-detail');
    if (path) {
      void this.router.navigate(this.dynamicRouteService.replacePathSegments(path, [task.id]));
    }
  }

  // La copia è una proposta nuova: gli avvisi dicono cosa è cambiato rispetto all'ordine
  duplicate(): void {
    const detail = this.detail()!;
    const request = detail.ordine ? { idOrdine: detail.ordine.id } : { idCarrello: detail.idCarrello ?? undefined };

    this.busy.set(true);
    this.proposalService.duplicate(request).pipe(finalize(() => this.busy.set(false))).subscribe(proposal => {
      if (proposal.avvisi.length > 0) {
        this.snackBar.open(proposal.avvisi.join(' '), translate<string>('orders.ui.chiudi'), { duration: 8000 });
      }
      void this.router.navigate(['/new-order-proposal'], { queryParams: { id: proposal.id } });
    });
  }

  downloadQuote(): void {
    this.proposalService.downloadQuote(this.detail()!.ordine!.id).subscribe(quote => downloadBlob(quote.blob, quote.fileName));
  }

  downloadAttachment(attachment: { id: string; filePath: string }, taskId: string): void {
    this.taskDetailService.downloadAttachmentService(taskId, attachment.id).subscribe({
      next: (blob: Blob) => downloadBlob(blob, this.fileName(attachment.filePath)),
      error: () => this.snackBar.open(translate<string>('orders.ui.allegatoNonDisponibile'), translate<string>('orders.ui.chiudi'), { duration: 5000 }),
    });
  }

  fileName(path: string): string {
    return path.split('/').pop() || path;
  }

  private onLoaded({ detail, alerts }: OrderDetailResult): void {
    this.detail.set(detail);
    this.alerts.set(alerts);

    const number = detail.ordine?.numero ?? detail.commesse.map(c => c.numeroCommessa).filter(n => n != null).join(', ');
    this.headerService.setTitle(translate<string>('orders.ui.dettaglioOrdine2', { value0: number }));

    // Lo storico c'è per gli ordini nati in App, su tutte le loro versioni
    const ordine = detail.ordine;
    if (ordine) {
      this.taskDetailService.loadOrderActivities(ordine.id).subscribe(activities =>
        this.history.set(historyFromActivities(activities ?? [], ordine.idTaskApprovativo ?? '')));
    }
  }

  private toViews(detail: OrderDetailData): OrderDetailView[] {
    const ordine = detail.ordine;

    if (detail.commesse.length === 0) {
      return ordine
        ? [{
            id: ordine.id,
            label: null,
            tag: orderTagLabel({
              statoApp: ordine.stato,
              statoErp: null,
              inAutorizzazione: ordine.stato === 'InAutorizzazione',
              bloccoAmministrativo: false,
              bloccoStock: false,
            }),
            tasks: detail.tasks,
            anomalies: (ordine.authorizationCases ?? []).map(c => c.descrizione),
            shipping: null,
            numeroOrdine: String(ordine.numero),
            dataInserimento: detail.dataInvio,
            provvigione: ordine.totaleProvvigioni,
            deposito: null,
            products: ordine.carrello.righe.filter(row => !row.archiviata).map(row => ({
              id: row.id,
              nome: row.nomeProdotto,
              sku: row.sku,
              imballo: row.nomeImballo,
              unitaFatturabile: row.unitaFatturabile,
              unitaMisura: row.unitaMisura,
              quantita: row.quantita,
              importo: row.imponibile,
            })),
            notes: (ordine.note ?? []).map(note => ({ title: note.titolo || translate<string>('orders.ui.ordine'), text: note.testo })),
          }]
        : [];
    }

    return detail.commesse.map(c => ({
      id: c.id,
      label: translate<string>('orders.ui.ordineN', { value0: c.numeroCommessa ?? c.idCommessa }),
      tag: orderTagLabel({
        statoApp: null,
        statoErp: c.stato,
        inAutorizzazione: c.inAutorizzazione,
        bloccoAmministrativo: c.bloccoAmministrativo,
        bloccoStock: c.bloccoStock,
      }),
      tasks: c.tasks,
      anomalies: [...new Set(c.tasks.flatMap(task => taskAnomalies(task.descrizione)))],
      shipping: c.stato && SHIPPED_STATES.includes(c.stato)
        ? { trasportatore: c.trasportatore, data: c.dataConsegna ?? c.dataSpedizione, consegnato: !!c.dataConsegna }
        : null,
      numeroOrdine: String(ordine?.numero ?? c.numeroCommessa ?? c.idCommessa),
      dataInserimento: ordine ? detail.dataInvio : c.dataCommessa,
      provvigione: c.provvigione,
      deposito: c.deposito,
      products: c.righe.map(row => ({
        id: row.id,
        nome: row.descrizioneProdotto,
        sku: row.sku,
        imballo: row.nomeImballo,
        unitaFatturabile: row.unitaFatturabile,
        unitaMisura: row.unitaMisura,
        quantita: row.qtaImballi,
        importo: row.qtaFatturabile * row.prezzo,
      })),
      notes: [
        { get title() { return translate<string>('orders.ui.ordine'); }, text: c.notaOrdine },
        { get title() { return translate<string>('orders.ui.bolla'); }, text: c.notaParticolare },
      ].filter((note): note is { title: string; text: string } => !!note.text),
    }));
  }
}
